import {
  BarList,
  Button,
  DataTable,
  EmptyState,
  formatCentsBRL,
  formatCompactCentsBRL,
  formatCount,
  formatMonth,
  formatNumber,
  formatPercent,
  formatRelativeTime,
  MetricCard,
  PageLayout,
  SectionCard,
  SegmentedControl,
  Spinner,
  TimeSeriesChart,
} from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowDownRightIcon,
  LineChartIcon,
  RefreshCwIcon,
  RepeatIcon,
  UsersIcon,
  WalletIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { ErrorState } from '@/components/error-state'
import { useRevenue, useSyncRevenue } from '@/hooks/use-revenue'
import type { RevenueMonth, RevenueOverview } from '@/lib/api-types'
import {
  MRR_SOURCE_HINTS,
  MRR_SOURCE_LABELS,
  translateStatus,
} from '@/lib/labels'

const PERIODS = [
  { value: '6', label: '6 meses' },
  { value: '12', label: '12 meses' },
  { value: '24', label: '24 meses' },
] as const

type RevenueSearch = { meses?: 6 | 24 }

export const Route = createFileRoute('/_app/receita')({
  validateSearch: (search: Record<string, unknown>): RevenueSearch => ({
    meses: search.meses === 6 || search.meses === 24 ? search.meses : undefined,
  }),
  component: RevenuePage,
})

function RevenuePage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const months = search.meses ?? 12
  const revenue = useRevenue(months)
  const sync = useSyncRevenue()
  const data = revenue.data

  const layout = {
    icon: LineChartIcon,
    title: 'Receita',
    description: 'MRR, movimento mês a mês, churn e funil de novos clientes.',
    meta: data ? <SyncMeta data={data} /> : undefined,
    actions: (
      <Button
        variant="outline"
        disabled={sync.isPending}
        onClick={() => sync.mutate()}
      >
        {sync.isPending ? <Spinner /> : <RefreshCwIcon />}
        Sincronizar agora
      </Button>
    ),
  }

  if (revenue.isError) {
    return (
      <PageLayout {...layout}>
        <ErrorState onRetry={() => void revenue.refetch()} />
      </PageLayout>
    )
  }

  // Último mês fechado: o atual ainda está andando.
  const closed = data?.months.slice(0, -1).filter((row) => !row.partial)
  const lastClosed = closed?.at(-1)

  return (
    <PageLayout {...layout} contentClassName="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl
          label="Período"
          options={PERIODS}
          value={String(months)}
          onChange={(value) =>
            void navigate({
              search: {
                meses: value === '12' ? undefined : (Number(value) as 6 | 24),
              },
              replace: true,
            })
          }
        />
        {data?.history.firstSnapshotDate ? (
          <p className="text-muted-foreground text-sm">
            Histórico desde{' '}
            {new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(
              new Date(`${data.history.firstSnapshotDate}T12:00:00Z`)
            )}
            .
          </p>
        ) : null}
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="MRR"
          value={formatCentsBRL(data?.current.mrrCents)}
          hint={`ARR ${formatCentsBRL((data?.current.mrrCents ?? 0) * 12)}`}
          icon={WalletIcon}
          loading={!data}
        />
        <MetricCard
          label="Clientes pagantes"
          value={formatNumber(data?.current.payingOrganizations)}
          hint={`Ticket médio ${formatCentsBRL(data?.current.arpaCents)}`}
          icon={UsersIcon}
          loading={!data}
        />
        <MetricCard
          label="Churn de receita"
          value={
            lastClosed?.grossChurnRate != null
              ? formatPercent(lastClosed.grossChurnRate)
              : '—'
          }
          hint={
            lastClosed
              ? `${formatMonth(lastClosed.month)} · cancelados e reduções`
              : 'Precisa de um mês fechado com histórico'
          }
          icon={ArrowDownRightIcon}
          tone={
            lastClosed?.grossChurnRate != null &&
            lastClosed.grossChurnRate > 0.05
              ? 'warning'
              : 'default'
          }
          loading={!data}
        />
        <MetricCard
          label="Retenção líquida (NRR)"
          value={
            lastClosed?.netRevenueRetention != null
              ? formatPercent(lastClosed.netRevenueRetention)
              : '—'
          }
          hint={
            lastClosed
              ? `${formatMonth(lastClosed.month)} · meta: acima de 100%`
              : 'Precisa de um mês fechado com histórico'
          }
          icon={RepeatIcon}
          tone={
            lastClosed?.netRevenueRetention != null
              ? lastClosed.netRevenueRetention >= 1
                ? 'success'
                : 'warning'
              : 'default'
          }
          loading={!data}
        />
      </div>

      <SectionCard
        title="MRR por dia"
        description="Fotos diárias às 06:00. Antes da primeira foto não há histórico."
      >
        {!data ? (
          <div className="h-72" />
        ) : data.history.daily.length >= 2 ? (
          <TimeSeriesChart
            data={data.history.daily}
            series={[{ key: 'mrrCents', label: 'MRR' }]}
            formatValue={formatCentsBRL}
            formatAxisValue={formatCompactCentsBRL}
            renderDetail={(point) =>
              `${formatNumber(point.payingOrganizations)} pagantes`
            }
          />
        ) : (
          <EmptyState
            variant="inline"
            icon={LineChartIcon}
            title="Histórico em formação"
            description="O gráfico aparece a partir da segunda foto diária. Use “Sincronizar agora” para registrar a de hoje."
          />
        )}
      </SectionCard>

      {data ? (
        <div className="grid items-start gap-6 xl:grid-cols-2">
          <SectionCard title="MRR por plano">
            <BarList
              max={data.current.mrrCents}
              items={data.current.byPlan.map((row) => ({
                key: row.plan,
                label: translateStatus(row.plan) ?? row.plan,
                value: row.mrrCents,
                display: formatCentsBRL(row.mrrCents),
                hint: `${formatNumber(row.organizations)} ${row.organizations === 1 ? 'cliente' : 'clientes'} · ${formatPercent(row.mrrCents / Math.max(1, data.current.mrrCents), 0)} do MRR`,
              }))}
            />
          </SectionCard>
          <SectionCard
            title="Origem do valor"
            description="Quanto do MRR é exato e quanto é estimativa."
          >
            <BarList
              max={data.current.mrrCents}
              items={data.current.bySource.map((row) => ({
                key: row.source,
                label: MRR_SOURCE_LABELS[row.source],
                value: row.mrrCents,
                display: formatCentsBRL(row.mrrCents),
                hint: `${formatNumber(row.organizations)} · ${MRR_SOURCE_HINTS[row.source]}`,
                color:
                  row.source === 'CATALOG'
                    ? 'var(--muted-foreground)'
                    : undefined,
              }))}
            />
          </SectionCard>
        </div>
      ) : null}

      <SectionCard
        title="Movimento do MRR"
        description="O que entrou e o que saiu em cada mês. Mês sem foto anterior fica sem taxas."
        contentClassName="p-0"
      >
        <DataTable
          variant="flush"
          rows={monthsWithData(data?.months ?? [])}
          loading={!data}
          getRowKey={(row) => row.month}
          empty="Sem meses no período."
          columns={[
            {
              key: 'month',
              header: 'Mês',
              render: (row) => (
                <div>
                  <p className="font-medium">{formatMonth(row.month)}</p>
                  {row.partial ? (
                    <p className="text-muted-foreground text-xs">
                      histórico começou no mês
                    </p>
                  ) : null}
                </div>
              ),
            },
            money('Início', (row) => row.startMrrCents),
            money('Novo', (row) => row.newCents, 'gain'),
            money('Expansão', (row) => row.expansionCents, 'gain'),
            money('Reativação', (row) => row.reactivationCents, 'gain'),
            money('Contração', (row) => row.contractionCents, 'loss'),
            money('Churn', (row) => row.churnCents, 'loss'),
            {
              key: 'net',
              header: 'Líquido',
              align: 'right',
              render: (row) =>
                row.endMrrCents == null ? (
                  '—'
                ) : (
                  <span className="font-medium">
                    {row.netNewCents > 0 ? '+' : ''}
                    {formatCentsBRL(row.netNewCents)}
                  </span>
                ),
            },
            {
              key: 'churnRate',
              header: 'Churn %',
              align: 'right',
              render: (row) =>
                row.grossChurnRate == null
                  ? '—'
                  : formatPercent(row.grossChurnRate),
            },
            {
              key: 'nrr',
              header: 'NRR',
              align: 'right',
              render: (row) =>
                row.netRevenueRetention == null
                  ? '—'
                  : formatPercent(row.netRevenueRetention, 0),
            },
            {
              key: 'received',
              header: 'Recebido',
              align: 'right',
              render: (row) => (
                <div>
                  <p>{formatCentsBRL(row.receivedCents)}</p>
                  {row.failedPayments > 0 ? (
                    <p className="text-destructive-soft-foreground text-xs">
                      {formatCount(row.failedPayments, 'falha', 'falhas')}
                    </p>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      </SectionCard>

      <SectionCard
        title="Funil de novas organizações"
        description="Organizações criadas em cada mês e até onde chegaram hoje."
        contentClassName="p-0"
      >
        <DataTable
          variant="flush"
          rows={[...(data?.funnel ?? [])].reverse()}
          loading={!data}
          getRowKey={(row) => row.month}
          empty="Sem organizações novas no período."
          columns={[
            {
              key: 'month',
              header: 'Mês de criação',
              render: (row) => (
                <span className="font-medium">{formatMonth(row.month)}</span>
              ),
            },
            {
              key: 'created',
              header: 'Criadas',
              align: 'right',
              render: (row) => formatNumber(row.created),
            },
            funnelStep('Onboarding concluído', (row) => row.onboarded),
            funnelStep('WhatsApp conectado', (row) => row.whatsappConnected),
            funnelStep('Pagando hoje', (row) => row.paying),
          ]}
        />
      </SectionCard>

      {data ? (
        <p className="text-muted-foreground text-xs">
          MRR = assinaturas ativas ou em atraso, sem teste nem cortesia. O valor
          vem da Stripe (com cupom) quando sincronizado, senão do contrato ou do
          preço de tabela. Churn % = (churn + contração) ÷ MRR do início do mês.
          NRR = (início − churn − contração + expansão + reativação) ÷ início.
          Meses no fuso {data.timeZone}.
        </p>
      ) : null}
    </PageLayout>
  )
}

/**
 * Do mais recente ao mais antigo, sem os meses anteriores ao primeiro dado
 * (foto ou fatura): eles só repetiriam "—".
 */
function monthsWithData(months: RevenueMonth[]) {
  const first = months.findIndex(
    (row) =>
      row.endMrrCents != null || row.receivedCents > 0 || row.failedPayments > 0
  )
  return first === -1 ? [] : months.slice(first).reverse()
}

function SyncMeta({ data }: { data: RevenueOverview }) {
  if (!data.stripeConfigured) {
    return (
      <span className="text-muted-foreground text-xs">
        Stripe não configurada no servidor
      </span>
    )
  }
  return (
    <span className="text-muted-foreground text-xs">
      {data.lastStripeSyncAt
        ? `Stripe sincronizada ${formatRelativeTime(data.lastStripeSyncAt)}`
        : 'Stripe ainda não sincronizada'}
    </span>
  )
}

function money(
  header: string,
  value: (row: RevenueMonth) => number | null,
  kind?: 'gain' | 'loss'
) {
  return {
    key: header,
    header,
    align: 'right' as const,
    render: (row: RevenueMonth): ReactNode => {
      const cents = value(row)
      if (cents == null || (kind && row.endMrrCents == null)) return '—'
      if (kind && cents === 0) {
        return <span className="text-muted-foreground">—</span>
      }
      return (
        <span
          className={
            kind === 'loss' ? 'text-destructive-soft-foreground' : undefined
          }
        >
          {kind === 'loss' ? '−' : kind === 'gain' ? '+' : ''}
          {formatCentsBRL(cents)}
        </span>
      )
    },
  }
}

type FunnelRow = RevenueOverview['funnel'][number]

function funnelStep(header: string, value: (row: FunnelRow) => number) {
  return {
    key: header,
    header,
    align: 'right' as const,
    render: (row: FunnelRow): ReactNode => (
      <div>
        <p>{formatNumber(value(row))}</p>
        {row.created > 0 ? (
          <p className="text-muted-foreground text-xs">
            {formatPercent(value(row) / row.created, 0)}
          </p>
        ) : null}
      </div>
    ),
  }
}
